/**
 * @fileoverview `RelatedShelf` — "More like this" shelf for the event detail page.
 *
 * Per the `ev-related` block in
 * `docs/design-system/1.7.0/templates/city/event.html` (CURRENT pin).
 *
 * Renders up to four related future events plus a section-header
 * "See all" CTA. WEB-1228 (R10): the editorial-pick icon on the date line
 * (`isTopPick`) was removed — no editorial write path exists. The caller resolves `seeAllHref` to the real category route
 * (`/category/<slug>`) with a `/discover` fallback — never `#`.
 *
 * Grid tracks match DS CURRENT (FRE-1077): 1 col → 2 cols at ≥541px → 4 cols
 * at ≥981px (template: `repeat(4)` default, `max-width: 980px` → 2,
 * `max-width: 540px` → 1).
 *
 * Pure presentational; the view returns `null` when no events are available
 * (honest empty state — no empty grid, no "no related events" copy). Each
 * card receives a pre-formatted, server-authoritative `dateLabel` string; the
 * view performs **no** client-side timezone recompute (the caller formats via
 * the shared timezone-safe formatter before passing props).
 *
 * @since WEB-208
 */
import * as React from 'react';
import Link from 'next/link';
import { SurfaceLink } from '@/components/atoms/SurfaceLink';
import { TextIcon } from '@/components/atoms/TextIcon';
import { cn } from '@/components/atoms/primitives/utils';

export interface RelatedShelfEvent {
  href: string;
  dateLabel: string;
  title: string;
  metaLabel: string;
}

export interface RelatedShelfViewProps {
  events: readonly RelatedShelfEvent[];
  totalInCategoryCount: number;
  categoryLabel: string;
  seeAllHref: string;
  className?: string;
}

const HEADING_ID = 'related-shelf-heading';
const MAX_VISIBLE_EVENTS = 4;

export function RelatedShelfView({
  events,
  totalInCategoryCount,
  categoryLabel,
  seeAllHref,
  className,
}: RelatedShelfViewProps): React.ReactElement | null {
  if (events.length === 0) {
    return null;
  }

  const visibleEvents = events.slice(0, MAX_VISIBLE_EVENTS);

  return (
    <section
      aria-labelledby={HEADING_ID}
      className={cn('flex flex-col gap-5 border-t border-border/60 py-10', className)}
      data-testid="related-shelf"
    >
      <header className="flex items-baseline justify-between gap-4">
        <h2 id={HEADING_ID} className="text-2xl font-semibold tracking-tight text-foreground">
          More like this
        </h2>
        <SurfaceLink
          href={seeAllHref}
          className="inline-flex min-h-11 desktop:min-h-0 items-center gap-1 rounded-sm px-2 font-mono text-xs tracking-wide text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          data-testid="related-shelf-see-all"
        >
          See all {totalInCategoryCount} {categoryLabel}
          <TextIcon />
        </SurfaceLink>
      </header>
      <ul
        data-testid="related-shelf-grid"
        className="grid grid-cols-1 gap-4 min-[541px]:grid-cols-2 min-[981px]:grid-cols-4"
      >
        {visibleEvents.map((event) => (
          <li key={event.href}>
            <Link
              href={event.href}
              className="flex h-full flex-col gap-2 rounded-md border border-border/60 bg-card p-4 transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              data-testid="related-shelf-card"
            >
              <span
                className="inline-flex items-center gap-1 font-mono text-micro tracking-wide text-primary"
                data-testid="related-shelf-card-date"
              >
                {event.dateLabel}
              </span>
              <h3 className="text-base font-semibold text-foreground">{event.title}</h3>
              <span className="mt-auto border-t border-border/60 pt-2 font-mono text-micro tracking-wide text-muted-foreground">
                {event.metaLabel}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface RelatedShelfProps extends RelatedShelfViewProps {}

export function RelatedShelf(props: RelatedShelfProps): React.ReactElement | null {
  return <RelatedShelfView {...props} />;
}
